import { useEffect, useState } from 'react';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Package, User as UserIcon, ArrowLeft, Trash2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Profile() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200 cursor-pointer';
      case 'advance_paid':
        return 'bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200 cursor-pointer';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusLabel = (order) => {
    if (order.status === 'advance_paid') {
      return `50% PAID - Balance ₹${Math.round(order.balance_due || order.total / 2).toLocaleString()} Due`;
    }
    return order.status?.toUpperCase();
  };

  const handleStatusClick = (order) => {
    if (order.status === 'pending') {
      // Navigate to payment page for pending orders
      navigate(`/payment/${order.id}`);
    } else if (order.status === 'advance_paid') {
      // Navigate to balance payment
      handleBalancePayment(order);
    }
  };

  const handleBalancePayment = async (order) => {
    try {
      // Load Razorpay script if not already loaded
      if (!document.getElementById('razorpay-script')) {
        const script = document.createElement('script');
        script.id = 'razorpay-script';
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }

      // Create balance payment order
      const response = await axios.post(
        `${API_URL}/payment/create-balance-order`,
        { order_id: order.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { razorpay_order_id, razorpay_key_id, amount, currency, balance_due } = response.data;

      // Open Razorpay checkout
      const options = {
        key: razorpay_key_id,
        amount: amount,
        currency: currency,
        name: 'Modular Furniture Store',
        description: `Balance Payment - Order #${order.id.substring(0, 8)}`,
        image: 'https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png',
        order_id: razorpay_order_id,
        handler: async function (paymentResponse) {
          try {
            const verifyResponse = await axios.post(
              `${API_URL}/payment/verify`,
              {
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
                order_id: order.id,
                payment_type: 'balance'
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (verifyResponse.data.success) {
              toast.success('Balance payment successful! Your order is confirmed for dispatch.');
              fetchOrders(); // Refresh orders
              navigate(`/thank-you/${order.id}?payment=balance`);
            }
          } catch (error) {
            console.error('Balance payment verification failed:', error);
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: order?.buyer_details?.name || user?.name || '',
          email: order?.buyer_details?.email || user?.email || '',
          contact: order?.buyer_details?.contact_number || ''
        },
        notes: {
          order_id: order.id,
          payment_type: 'balance'
        },
        theme: {
          color: '#22c55e'
        },
        modal: {
          ondismiss: function () {
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
      });
      razorpay.open();

    } catch (error) {
      console.error('Error initiating balance payment:', error);
      toast.error('Failed to initiate balance payment. Please try again.');
    }
  };

  const handleCheckboxChange = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Please select orders to delete');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to cancel ${selectedOrders.length} pending order(s)? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeleting(true);
    try {
      // Delete each selected order
      await Promise.all(
        selectedOrders.map(orderId =>
          axios.delete(`${API_URL}/orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );

      toast.success(`${selectedOrders.length} order(s) cancelled successfully`);
      setSelectedOrders([]);
      fetchOrders(); // Refresh orders list
    } catch (error) {
      console.error('Error deleting orders:', error);
      toast.error('Failed to cancel orders. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const advancePaidOrders = orders.filter(o => o.status === 'advance_paid');
  const hasPendingOrders = pendingOrders.length > 0;
  const hasBalanceDue = advancePaidOrders.length > 0;

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen noise-texture">
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Previous Page
        </button>

        <h1 className="text-5xl md:text-6xl font-black uppercase mb-12">MY PROFILE</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Info */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-border rounded-none p-4 sm:p-8">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-accent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <UserIcon className="w-12 h-12 text-accent-foreground" />
                </div>
                <h2 className="text-2xl font-black uppercase" data-testid="profile-name">{user.name}</h2>
                <p className="mono text-sm text-muted-foreground" data-testid="profile-email">{user.email}</p>
              </div>
              {user.is_admin && (
                <div className="mt-4 p-3 bg-accent text-accent-foreground text-center rounded-none">
                  <p className="text-xs uppercase tracking-wider font-bold">ADMIN USER</p>
                </div>
              )}
            </div>
          </div>

          {/* Orders */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-border rounded-none p-4 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <Package className="w-6 h-6" />
                  <h2 className="text-2xl font-black uppercase">MY ORDERS</h2>
                </div>
                
                {/* Delete Selected Button - Only show if pending orders are selected */}
                {selectedOrders.length > 0 && (
                  <Button
                    onClick={handleDeleteSelected}
                    disabled={deleting}
                    variant="destructive"
                    className="rounded-none uppercase tracking-wider text-xs font-bold"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {deleting ? 'CANCELLING...' : `CANCEL ${selectedOrders.length} ORDER(S)`}
                  </Button>
                )}
              </div>

              {/* Info Banner for Pending Orders */}
              {hasPendingOrders && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-none p-4 mb-6">
                  <p className="text-sm text-yellow-800">
                    <strong>💡 Tip:</strong> Click on <span className="font-bold bg-yellow-100 px-2 py-0.5 border border-yellow-300">PENDING</span> to complete your payment. 
                    Use checkboxes to select and cancel unwanted orders.
                  </p>
                </div>
              )}

              {/* Info Banner for Orders with Balance Due */}
              {hasBalanceDue && (
                <div className="bg-orange-50 border border-orange-200 rounded-none p-4 mb-6">
                  <p className="text-sm text-orange-800">
                    <strong>💰 Balance Due:</strong> You have {advancePaidOrders.length} order(s) with pending balance payment. 
                    Click on <span className="font-bold bg-orange-100 px-2 py-0.5 border border-orange-300">50% PAID</span> to pay the remaining balance before dispatch.
                  </p>
                </div>
              )}

              {loading ? (
                <p className="text-center py-12 text-muted-foreground">Loading orders...</p>
              ) : orders.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground">No orders yet</p>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      data-testid={`order-${order.id}`}
                      className={`border border-border rounded-none p-4 sm:p-6 hover:border-accent transition-colors ${
                        selectedOrders.includes(order.id) ? 'border-red-400 bg-red-50' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-start gap-3">
                          {/* Checkbox for Pending Orders */}
                          {order.status === 'pending' && (
                            <input
                              type="checkbox"
                              checked={selectedOrders.includes(order.id)}
                              onChange={() => handleCheckboxChange(order.id)}
                              className="mt-1 w-5 h-5 rounded-none border-2 border-gray-300 cursor-pointer accent-red-500"
                              title="Select to cancel this order"
                            />
                          )}
                          <div>
                            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                              ORDER ID
                            </p>
                            <p className="mono font-bold">{order.id}</p>
                          </div>
                        </div>
                        
                        {/* Status Badge - Clickable for Pending and Advance Paid */}
                        <div className="flex items-center gap-2">
                          {(order.status === 'pending' || order.status === 'advance_paid') && (
                            <CreditCard className={`w-4 h-4 ${order.status === 'advance_paid' ? 'text-orange-600' : 'text-yellow-600'}`} />
                          )}
                          <button
                            onClick={() => handleStatusClick(order)}
                            className={`px-3 py-1 border rounded-none text-xs uppercase tracking-wider font-bold transition-all ${getStatusColor(order.status)} ${
                              (order.status === 'pending' || order.status === 'advance_paid') ? 'hover:scale-105 shadow-sm' : ''
                            }`}
                            disabled={order.status !== 'pending' && order.status !== 'advance_paid'}
                            title={order.status === 'pending' ? 'Click to complete payment' : order.status === 'advance_paid' ? 'Click to pay balance' : ''}
                          >
                            {getStatusLabel(order)}
                            {order.status === 'pending' && ' →'}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span>{item.product_name} x {item.quantity}</span>
                            <span className="mono">{formatPrice(item.total)}</span>
                          </div>
                        ))}
                      </div>

                      <div className="border-t border-border pt-4 flex justify-between items-center">
                        <p className="text-xs text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                        <div className="flex items-center gap-4">
                          {order.status === 'pending' && (
                            <Button
                              onClick={() => navigate(`/payment/${order.id}`)}
                              size="sm"
                              className="rounded-none uppercase tracking-wider text-xs font-bold bg-primary hover:bg-primary/90"
                            >
                              <CreditCard className="w-3 h-3 mr-1" />
                              PAY NOW
                            </Button>
                          )}
                          <p className="mono font-black text-lg">{formatPrice(order.total)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
